import type { AssessmentProgress, SpeechQuestion } from '@/features/nectar/api/models'
import { getSpeechQuestions, submitSpeechAnswer } from '@/features/nectar/api/proficiency-assessment'
import { formatTime } from '@/features/nectar/components/audio-recording/AudioRecordingTask'
import { RecordingStateIdle } from '@/features/nectar/components/audio-recording/RecordingStateIdle'
import { RecordingStateRecorded } from '@/features/nectar/components/audio-recording/RecordingStateRecorded'
import { RecordingStateRecording } from '@/features/nectar/components/audio-recording/RecordingStateRecording'
import { NectarAudioPlayer } from '@/features/nectar/components/shared/NectarAudioPlayer'
import { useAudioRecorder } from '@/features/nectar/hooks/useAudioRecorder'
import { useRecordingQualityAnalytics } from '@/features/nectar/hooks/useRecordingQualityAnalytics'
import { Spinner, Text } from '@/ui-kit'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

const MIN_RECORDING_SEC = 10
const MAX_RECORDING_SEC = 60

type PhaseSpeechRecordingProps = {
  assessmentId: string
  onAllRecorded: () => void
  onProgress: (progress: AssessmentProgress) => void
}

export const PhaseSpeechRecording = ({ assessmentId, onAllRecorded, onProgress }: PhaseSpeechRecordingProps) => {
  const [questions, setQuestions] = useState<SpeechQuestion[]>([])
  const [currentIdx, setCurrentIdx] = useState(0)
  const [loading, setLoading] = useState(true)

  const {
    state,
    seconds,
    audioUrl,
    blob,
    startRecording,
    stopRecording,
    resetToIdle,
    markSubmitting,
    markRecorded,
    quality,
    analyser
  } = useAudioRecorder()
  useRecordingQualityAnalytics(assessmentId, quality)
  const secondsRef = useRef(seconds)
  secondsRef.current = seconds

  useEffect(() => {
    let cancelled = false
    getSpeechQuestions(assessmentId)
      .then((res) => {
        if (!cancelled) {
          setQuestions(res.questions)
          onProgress(res.progress)
          setLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) toast.error(t`Failed to load speech questions`)
      })
    return () => {
      cancelled = true
    }
  }, [assessmentId, onProgress])

  const submit = useCallback(async () => {
    if (!blob) return
    if (secondsRef.current < MIN_RECORDING_SEC) {
      toast.error(t`Recording must be at least ${MIN_RECORDING_SEC} seconds`)
      return
    }
    const question = questions[currentIdx]
    markSubmitting()

    try {
      const file = new File([blob], 'recording.wav', { type: 'audio/wav' })
      const formData = new FormData()
      formData.append('file', file)
      formData.append('questionId', question.id)

      const res = await submitSpeechAnswer(assessmentId, formData)
      onProgress(res.progress)
      const current = currentIdx + 1
      const total = questions.length
      toast.success(t`Recording ${current}/${total} uploaded`)

      if (currentIdx < questions.length - 1) {
        setTimeout(() => {
          setCurrentIdx((i) => i + 1)
          resetToIdle()
          // After reset, go back to idle by starting fresh
        }, 400)
      } else {
        setTimeout(() => onAllRecorded(), 400)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload recording')
      markRecorded()
    }
  }, [blob, questions, currentIdx, assessmentId, markSubmitting, resetToIdle, markRecorded, onAllRecorded, onProgress])

  if (loading) {
    return (
      <div className="flex w-full justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <Spinner />
          <Text type="body2-regular" className="text-gray-80">
            <Trans>Loading speech questions...</Trans>
          </Text>
        </div>
      </div>
    )
  }

  if (!questions.length) {
    return (
      <div className="flex w-full justify-center py-12">
        <Text type="body2-regular" className="text-gray-80">
          <Trans>No speech questions available.</Trans>
        </Text>
      </div>
    )
  }

  const currentQuestion = questions[currentIdx]
  const tooShort = seconds < MIN_RECORDING_SEC
  const pastSoftMax = seconds >= MAX_RECORDING_SEC

  return (
    <div
      className="mx-auto flex w-full max-w-[600px] flex-col gap-5"
      style={{ animation: 'nectar-slide-up 0.3s ease-out' }}
    >
      {state === 'idle' && (
        <Text type="body2-regular" className="text-gray-80">
          <Trans>🎧 Listen · 🎤 Answer · 10s+</Trans>
        </Text>
      )}

      {/* Card */}
      <div className="flex flex-col gap-5 rounded-2xl border border-solid border-gray-100 px-6 py-5">
        {/* Question audio */}
        <NectarAudioPlayer key={currentQuestion.id} audioUrl={currentQuestion.audioUrl} />

        {/* Recording timer hints */}
        {state === 'recording' && (
          <div className="flex flex-col items-center gap-1">
            <Text type="body2-regular" className="text-gray-90 font-mono">
              {formatTime(seconds)}
            </Text>
            {pastSoftMax ? (
              <Text type="body3-regular" className="text-red">
                <Trans>You can stop now</Trans>
              </Text>
            ) : (
              <Text type="body3-regular" className="text-gray-80">
                {MAX_RECORDING_SEC - seconds}s left
              </Text>
            )}
          </div>
        )}

        {/* Recording controls */}
        <div className="flex w-full flex-col items-center gap-5">
          {state === 'idle' && <RecordingStateIdle onStartRecording={startRecording} />}
          {state === 'recording' && (
            <RecordingStateRecording seconds={seconds} analyser={analyser} onStopRecording={stopRecording} />
          )}
          {(state === 'recorded' || state === 'submitting' || state === 'submitted') && (
            <>
              <RecordingStateRecorded
                audioUrl={audioUrl}
                seconds={seconds}
                isSubmitting={state === 'submitting'}
                minSeconds={MIN_RECORDING_SEC}
                quality={quality}
                onReRecord={() => {
                  resetToIdle()
                  startRecording()
                }}
                onSubmit={submit}
              />
              {tooShort && state !== 'submitting' && (
                <Text type="body3-regular" className="text-red">
                  Recording must be at least {MIN_RECORDING_SEC} seconds
                </Text>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
